import React from 'react';
import styled from 'styled-components';
import kindredLogo from '../../Assets/TreePlanting/white_kindred_logo.png';
import { mobileMediaQuery, themeColor, useBreakPoints } from '../../Theming';
import { useTranslation } from 'react-i18next';
import { useTreePlantedContext } from '../TreeplantingContext';
import env from '../../env';
import CountUp from 'react-countup';

const HeroContainer = styled.div`
  width: 100%;
  height: 860px;
  display: flex;
  text-align: center;
  justify-content: center;
`;

const HeroBottomContainer = styled.div`
  width: 100%;
  height: 251px;
  background-color: ${themeColor('forestGreen')};
  z-index: 2;
  position: absolute;
  top: 595px;
  text-align: center;

  ${mobileMediaQuery} {
    padding-top: 50px;
    height: 300px;
  }
`;

const HeroBottomRoundContainer = styled.div`
  position: absolute;
  width: 100%;
  height: 200px;
  top: 520px;
  background-color: ${themeColor('forestGreen')};
  z-index: 1;
  border-radius: 50%;

  ${mobileMediaQuery} {
    height: 60px;
    top: 570px;
  }
`;

const HeroImage = styled.img`
  object-fit: cover;
  width: 100%;
  max-height: 800px;
  z-index: -1;

  ${mobileMediaQuery} {
    object-fit: cover;
    width: 100%;
    height: 100%;
    background-repeat: no-repeat;
  }
`;

const LogoImage = styled.img`
  width: 276px;
  max-height: 87px;
  position: absolute;
  top: 172px;
  text-align: center;
`;

const HeaderContainer = styled.div`
  width: 60%;
  height: 150px;
  z-index: 1;
  position: absolute;
  top: 300px;
  text-align: center;

  ${mobileMediaQuery} {
    width: 100%;
    top: 236px;
    max-width: 397px;
    margin-left: 20px;
    margin-right: 20px;
  }
`;

const HeaderText = styled.p`
  font-style: normal;
  font-weight: 400;
  font-size: 38px;
  line-height: 54px;
  text-align: center;
  color: ${themeColor('white')};
  z-index: 1;
  margin-top: 0px;
  margin-bottom: 0px;

  ${mobileMediaQuery} {
    font-size: 21px;
    line-height: 38px;
  }
`;

const HeaderTextBold = styled(HeaderText)`
  font-weight: 700;

  ${mobileMediaQuery} {
    font-size: 29px;
    line-height: 38px;
  }
`;

const TressAmountContainer = styled.div`
  width: 100%;
  height: 150px;
  z-index: 1;
  display: flex;
  justify-content: center;

  ${mobileMediaQuery} {
    display: block;
    text-align: center;
  }
`;

const TreesPlantedText = styled(HeaderText)`
  font-weight: 300;
  width: 100%;
  color: ${themeColor('white')};

  ${mobileMediaQuery} {
    font-size: 23px;
  }
`;

const TreesText = styled(HeaderText)`
  font-weight: 700;
  font-size: 77px;
  line-height: 92px;

  ${mobileMediaQuery} {
    font-size: 50px;
    margin: 0px;
    line-height: 60px;
  }
`;

const TreesPlantedAmount = styled(TreesText)`
  font-weight: 700;
  margin-right: 20px;
  color: ${themeColor('pastelGreen')};

  ${mobileMediaQuery} {
    margin: 0px;
  }
`;

interface Props {
  bgImage?: string;
  headerText?: string;
  subText?: string;
}

const Hero: React.FC<Props> = ({ bgImage, headerText, subText }) => {
  const { t } = useTranslation();
  const { mobile } = useBreakPoints();

  const { treesPlanted, partnerLogo, showTreesPlantedByPartner } =
    useTreePlantedContext();

  const imgSrc = showTreesPlantedByPartner
    ? `${env.cdn_url}/${partnerLogo}`
    : kindredLogo;

  return (
    <HeroContainer>
      <HeroImage src={bgImage} />
      {!mobile && <LogoImage src={imgSrc} />}
      <HeaderContainer>
        <HeaderTextBold>{headerText}</HeaderTextBold>
        <HeaderText>{subText}</HeaderText>
      </HeaderContainer>
      <HeroBottomRoundContainer></HeroBottomRoundContainer>
      <HeroBottomContainer>
        <TreesPlantedText>
          {t('treePlanting.hero.treesPlanted')}
        </TreesPlantedText>
        <TressAmountContainer>
          <TreesPlantedAmount>
            <CountUp start={0} end={treesPlanted as number} duration={2} />
          </TreesPlantedAmount>
          <TreesText>{t('treePlanting.hero.trees')}</TreesText>
        </TressAmountContainer>
      </HeroBottomContainer>
    </HeroContainer>
  );
};

export default Hero;
